import { parseMarkdownToBlocks, parseLayout, generateBlockId } from './chartParser';

/**
 * Migrate a V1 chart (markdown + layout) to V2 format
 * Simplified V2: every block has header (string) + content (monospace), no type field
 * @param {string} markdown - The V1 markdown chart content
 * @param {string} layoutJson - The V1 chart_layout JSON string (optional)
 * @returns {Object} V2 chart object with version, blocks, and layout
 */
export function migrateChartV1toV2(markdown, layoutJson) {
  // Parse V1 markdown into blocks
  const v1Blocks = parseMarkdownToBlocks(markdown);

  // Convert blocks to simplified V2 format with generated IDs
  const v2Blocks = v1Blocks.map((block, index) => {
    let header = 'Section';
    let content = block.content || '';

    // Handle attached headers (from V1 blocks that have a header property)
    if (block.header) {
      header = block.header.title || 'Section';
    }

    // Handle standalone header blocks (where type is h1/h2/h3)
    if (block.type === 'h1' || block.type === 'h2' || block.type === 'h3') {
      header = block.title || block.content || 'Section';
      content = ''; // For standalone headers, the content was the title
    }

    return {
      id: generateBlockId(),
      header,
      content
    };
  });

  // Filter out empty blocks (blocks with no header and no content)
  const filteredBlocks = v2Blocks.filter(block =>
    block.header !== 'Section' || block.content.trim() !== ''
  );

  // Ensure at least one block
  const finalBlocks = filteredBlocks.length > 0 ? filteredBlocks : [{
    id: generateBlockId(),
    header: 'Verse',
    content: ''
  }];

  // Parse and convert V1 layout
  const v1Layout = parseLayout(layoutJson);
  const v2Layout = convertLayoutIndices(v1Layout, finalBlocks);

  return {
    version: 2,
    blocks: finalBlocks,
    layout: v2Layout
  };
}

/**
 * Convert V1 index-based layout to V2 ID-based layout
 * @param {Object|null} v1Layout - The V1 layout object (parsed from chart_layout)
 * @param {Array} v2Blocks - The V2 blocks array (with IDs)
 * @returns {Object} V2 layout object with ID-based columns
 */
function convertLayoutIndices(v1Layout, v2Blocks) {
  // Default layout - all blocks in a single column
  const allBlockIds = v2Blocks.map(b => b.id);
  const defaultLayout = {
    portrait: { columns: [allBlockIds.slice()] },
    landscape: { columns: [allBlockIds.slice()] }
  };

  if (!v1Layout) {
    return defaultLayout;
  }

  const result = {};

  // Convert portrait layout if present
  if (v1Layout.portrait?.columns) {
    result.portrait = {
      columns: v1Layout.portrait.columns.map(columnIndices =>
        columnIndices.map(index => v2Blocks[index]?.id).filter(Boolean)
      )
    };
  } else {
    result.portrait = defaultLayout.portrait;
  }

  // Convert landscape layout if present
  if (v1Layout.landscape?.columns) {
    result.landscape = {
      columns: v1Layout.landscape.columns.map(columnIndices =>
        columnIndices.map(index => v2Blocks[index]?.id).filter(Boolean)
      )
    };
  } else {
    result.landscape = defaultLayout.landscape;
  }

  return result;
}
